import React from "react";

// App
import { ApiPath, Field, Notice, Section, TagList } from "./apiField";

const occupancyLabel = (room) => {
  const parts = [];
  if (room.maxAdults != null) {
    parts.push(`${room.maxAdults} adult${room.maxAdults === 1 ? "" : "s"}`);
  }
  if (room.maxChildren) {
    parts.push(`${room.maxChildren} child${room.maxChildren === 1 ? "" : "ren"}`);
  }
  return parts.join(" + ");
};

const bedLabel = (beds) =>
  beds
    .map((b) => (b.number ? `${b.number} x ${b.label}` : b.label))
    .join(", ");

// A room with no pictures has two very different causes and the screen says
// which: "dynamic" means the gallery document exists but deliberately carries
// no multimedia (the images are assembled downstream from ICE Portal), while a
// missing document means nothing was published for that room code at all.
const galleryNote = (room) => {
  if (room.images.length) return "";
  if (!room.hasGallery) return "No galleryRoom document for this room code";
  if (room.isGalleryDynamic) return "Gallery is dynamic: images come from ICE Portal, not this feed";
  return "Gallery document published with no images";
};

export default function RoomsSection({ rooms, features, raw }) {
  // Defaulted so a caller that omits a prop renders an empty block rather
  // than throwing on a length check.
  const list = rooms?.rooms || [];
  const intro = rooms?.intro || { title: "", paragraphs: [], paragraphsPath: "" };
  const featureList = features?.features || [];
  const featurePath = features?.path || "";

  return (
    <Section
      id="rooms"
      title="Guest rooms"
      subtitle={
        list.length
          ? `${list.length} room type${list.length === 1 ? "" : "s"}, ${
              rooms?.withGallery || 0
            } with images`
          : "No room types"
      }
      path="rooms.collection[]"
      raw={raw}
    >
      {intro.title || intro.paragraphs.length ? (
        <div className="rhp-intro">
          {intro.title ? <h3>{intro.title}</h3> : null}
          {intro.paragraphs.map((text, i) => (
            <p key={i}>{text}</p>
          ))}
          <ApiPath path={intro.paragraphsPath} />
        </div>
      ) : (
        <Notice tone="info">
          No rooms intro imported for this hotel. Copy comes from the content
          spreadsheet, not the API.
        </Notice>
      )}

      {featureList.length ? (
        <>
          <h3 className="rhp-subhead">
            In-room features (hotel level)
            <span className="rhp-badge rhp-badge--imported">Imported</span>
          </h3>
          <TagList items={featureList} path={featurePath} />
        </>
      ) : null}

      {!list.length ? (
        <Notice tone="warn">
          No room types published. <code>rooms.collection</code> is empty.
        </Notice>
      ) : (
        <div className="rhp-outlets">
          {list.map((room) => (
            <article className="rhp-outlet rhp-room" key={room.key || room.index}>
              {room.hero.url || room.images.length ? (
                <div className="rhp-outlet__media">
                  <img
                    src={
                      room.hero.thumb ||
                      room.hero.url ||
                      room.images[0]?.thumb ||
                      room.images[0]?.url
                    }
                    alt={room.hero.alt || room.name}
                    loading="lazy"
                  />
                  <ApiPath
                    path={room.hero.url ? room.hero.path : room.imagesPath}
                  />
                </div>
              ) : null}

              <div className="rhp-outlet__body">
                <header>
                  <h3>{room.name || room.key}</h3>
                  <ApiPath path={room.namePath} />
                  {room.key ? (
                    <div className="rhp-room__code">
                      <code>{room.key}</code>
                      <ApiPath path={room.keyPath} />
                    </div>
                  ) : null}
                </header>

                {room.paragraphs.map((text, i) => (
                  <p className="rhp-outlet__description" key={i}>
                    {text}
                  </p>
                ))}

                <div className="rhp-grid rhp-grid--tight">
                  <Field
                    label="Size"
                    value={room.size}
                    suffix={room.size ? room.metric : ""}
                    path={room.sizePath}
                  />
                  <Field
                    label="Max occupancy"
                    value={occupancyLabel(room)}
                    path={room.occupancyPath}
                  />
                  <Field
                    label="Beds"
                    value={bedLabel(room.beds)}
                    path={room.bedsPath}
                  />
                </div>

                <h4 className="rhp-subhead rhp-subhead--sm">In-room amenities</h4>
                <TagList items={room.amenities} path={room.amenitiesPath} />

                <h4 className="rhp-subhead rhp-subhead--sm">Room gallery</h4>
                {room.images.length ? (
                  <>
                    <div className="rhp-thumbs">
                      {room.images.slice(0, 8).map((img, i) => (
                        <a
                          key={`${room.key}-${i}`}
                          href={img.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={img.thumb || img.url}
                            alt={img.alt || room.name}
                            loading="lazy"
                          />
                        </a>
                      ))}
                    </div>
                    {room.images.length > 8 ? (
                      <p className="rhp-note">
                        Showing 8 of {room.images.length}
                      </p>
                    ) : null}
                    <ApiPath path={room.imagesPath} />
                  </>
                ) : (
                  <div className="rhp-taglist is-empty">
                    <span className="rhp-field__empty">
                      {galleryNote(room)}
                    </span>
                    <ApiPath path={room.imagesPath} />
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </Section>
  );
}
